import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ResetPassword.css';
import authApi from '../api/authApi';
import SparkApi from '../api/SparkApi';
import WatconnectApi from '../api/WatconnectApi';
import * as constants from '../constants/CONSTANT';
import jwt_decode from 'jwt-decode';

const ResetPassword = () => {
  const watconnectApi = new WatconnectApi('auth');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [username, setUsername] = useState('');
  const [schema, setSchema] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Password validation rules
  const passwordRules = [
    { test: pwd => pwd.length >= 8, message: 'At least 8 characters' },
    { test: pwd => /[A-Z]/.test(pwd), message: 'One uppercase letter' },
    { test: pwd => /[a-z]/.test(pwd), message: 'One lowercase letter' },
    { test: pwd => /[0-9]/.test(pwd), message: 'One number' },
    {
      test: pwd => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
      message: 'One special character',
    },
  ];

  const validatePassword = pwd => {
    const errors = passwordRules
      .filter(rule => !rule.test(pwd))
      .map(rule => rule.message);
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await axios.get(
          `${constants.API_BASE_URL}/api/reset-password/verify?token=${token}`
        );
        if (res.data?.valid) {
          setUsername(res.data.username);
          setSchema(res.data.schema);
          setValid(true);
        }
      } catch (err) {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    if (token) verifyToken();
  }, [token]);

  const handlePasswordChange = e => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleAutoLogin = async (username, userPassword) => {
    try {
      setIsLoggingIn(true);

      const result = await authApi.login(username, userPassword, schema);
      console.log('Auto-login result:', result);

      if (result.success) {
        localStorage.setItem('token', result.authToken);
        const decodedToken = jwt_decode(result.authToken);
        console.log('Decoded Token:', decodedToken);

        if (decodedToken.email) {
          sessionStorage.setItem('system_email', decodedToken.email);
        }

        if (decodedToken.watconnect) {
          try {
            const watLoginResponse = await watconnectApi.login({
              email: decodedToken.watconnect.username,
              password: decodedToken.watconnect.password,
              tcode: decodedToken.watconnect.company_name,
            });

            if (watLoginResponse && watLoginResponse.success) {
              console.log('WatConnect login success ✅');
            } else {
              console.warn('WatConnect login failed ❌', watLoginResponse);
            }
          } catch (watError) {
            console.warn('WatConnect login error:', watError);
          }
        }

        try {
          let notifications = await SparkApi.fetchSystemNotifications();
          if (notifications) {
            notifications.forEach(function (rec) {
              if (rec.type === 'SYSTEM_MAINTANANCE') {
                sessionStorage.setItem(
                  'notifications',
                  JSON.stringify(notifications)
                );
                return;
              }
            });
          } else {
            sessionStorage.removeItem('notifications');
          }
        } catch (notifError) {
          console.warn('Error fetching notifications:', notifError);
        }

        sessionStorage.setItem(
          'product_category',
          JSON.stringify(constants.Product_CATEGORY_VALUES)
        );
        sessionStorage.setItem(
          'product_subcategory',
          JSON.stringify(constants.Product_SUBCATEGORY_VALUES)
        );

        try {
          let settingResult = await SparkApi.fetchCompanySetting(
            'lead_status_setting'
          );
          if (settingResult && settingResult.setting) {
            sessionStorage.setItem(
              'lead_status',
              settingResult.setting.configuration
            );
          } else {
            sessionStorage.setItem(
              'lead_status',
              JSON.stringify(constants.LEAD_STATUS_VALUES)
            );
          }
        } catch (settingError) {
          console.warn('Error fetching settings:', settingError);
          sessionStorage.setItem(
            'lead_status',
            JSON.stringify(constants.LEAD_STATUS_VALUES)
          );
        }

        sessionStorage.setItem('selectedWhatsAppSetting', '');
        setMessage('✅ Password updated successfully! Logging you in...');

        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        throw new Error(result.errors || 'Login failed');
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setMessage('✅ Password updated! Please log in with your new password.');
      setIsLoggingIn(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validatePassword(password)) {
      setMessage('❌ Please fix password requirements');
      return;
    }

    if (password !== confirm) {
      setMessage('❌ Passwords do not match');
      return;
    }

    try {
      await axios.post(`${constants.API_BASE_URL}/api/reset-password`, {
        token,
        password,
      });
      await handleAutoLogin(username, password);
    } catch (err) {
      console.error('Password reset error:', err);
      setMessage('❌ Failed to update password. Token may have expired.');
    }
  };

  const getPasswordStrength = () => {
    const strength = passwordRules.filter(rule => rule.test(password)).length;
    const percentage = (strength / passwordRules.length) * 100;

    if (password.length === 0) return { width: '0%', color: '#e0e0e0' };
    if (percentage <= 40) return { width: `${percentage}%`, color: '#ff4444' };
    if (percentage <= 80) return { width: `${percentage}%`, color: '#ffbb33' };
    return { width: `${percentage}%`, color: '#00C851' };
  };

  // Full page loader
  if (loading) {
    return (
      <div className='reset-password-container'>
        <div className='reset-password-full-page-loader'>
          <div className='reset-password-loader-content'>
            <div className='reset-password-spinner'></div>
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className='reset-password-container'>
        <div className='reset-password-card reset-password-error-card'>
          <div className='reset-password-error-icon'>⚠️</div>
          <h3>Invalid or Expired Link</h3>
          <p>
            The password reset link is invalid or has expired. Please request a
            new password reset link.
          </p>
          <button
            className='reset-password-btn-secondary'
            onClick={() => navigate('/login')}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='reset-password-container'>
      {/* Full page overlay loader during auto-login */}
      {isLoggingIn && (
        <div className='reset-password-full-page-loader'>
          <div className='reset-password-loader-content'>
            <div className='reset-password-spinner'></div>
            <p>Logging you in...</p>
          </div>
        </div>
      )}

      <div className='reset-password-card'>
        <div className='reset-password-header'>
          <div className='reset-password-lock-icon'>
            <svg
              width='48'
              height='48'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M12 2C9.23858 2 7 4.23858 7 7V10H6C4.89543 10 4 10.8954 4 12V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V12C20 10.8954 19.1046 10 18 10H17V7C17 4.23858 14.7614 2 12 2Z'
                fill='#667eea'
              />
              <path
                d='M12 14C10.8954 14 10 14.8954 10 16C10 17.1046 10.8954 18 12 18C13.1046 18 14 17.1046 14 16C14 14.8954 13.1046 14 12 14Z'
                fill='white'
              />
            </svg>
          </div>
          <h2>Reset Your Password</h2>
          <p className='reset-password-email'>
            for <strong>{username}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className='reset-password-form'>
          <div className='reset-password-form-group'>
            <label htmlFor='password'>New Password</label>
            <div className='reset-password-input-container'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
                minLength={8}
                className={
                  passwordErrors.length > 0 && password.length > 0
                    ? 'reset-password-input-error'
                    : ''
                }
                placeholder='Enter your new password'
                disabled={isLoggingIn}
              />
              <button
                type='button'
                className='reset-password-toggle'
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoggingIn}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  {showPassword ? (
                    <>
                      <path
                        d='M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.74 7.01C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27ZM7.53 9.8L9.08 11.35C9.03 11.56 9 11.78 9 12C9 13.66 10.34 15 12 15C12.22 15 12.44 14.97 12.65 14.92L14.2 16.47C13.53 16.8 12.79 17 12 17C9.24 17 7 14.76 7 12C7 11.21 7.2 10.47 7.53 9.8ZM11.84 9.02L14.99 12.17L15.01 12.01C15.01 10.35 13.67 9.01 12.01 9.01L11.84 9.02Z'
                        fill='#666'
                      />
                    </>
                  ) : (
                    <>
                      <path
                        d='M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z'
                        fill='#666'
                      />
                    </>
                  )}
                </svg>
              </button>
            </div>

            {/* Password strength indicator */}
            {password.length > 0 && (
              <div className='reset-password-strength'>
                <div
                  className='reset-password-strength-bar'
                  style={getPasswordStrength()}
                ></div>
              </div>
            )}

            {/* Password requirements */}
            {password.length > 0 && (
              <div className='reset-password-requirements'>
                <p className='reset-password-requirements-title'>
                  Password Requirements:
                </p>
                <ul>
                  {passwordRules.map((rule, index) => (
                    <li
                      key={index}
                      className={
                        rule.test(password)
                          ? 'reset-password-requirement-valid'
                          : 'reset-password-requirement-invalid'
                      }
                    >
                      <span className='reset-password-requirement-icon'>
                        {rule.test(password) ? '✓' : '•'}
                      </span>
                      {rule.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className='reset-password-form-group'>
            <label htmlFor='confirm'>Confirm Password</label>
            <div className='reset-password-input-container'>
              <input
                id='confirm'
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                className={
                  confirm && password !== confirm
                    ? 'reset-password-input-error'
                    : ''
                }
                placeholder='Confirm your new password'
                disabled={isLoggingIn}
              />
              <button
                type='button'
                className='reset-password-toggle'
                onClick={() => setShowConfirm(!showConfirm)}
                disabled={isLoggingIn}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  {showConfirm ? (
                    <>
                      <path
                        d='M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.74 7.01C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27ZM7.53 9.8L9.08 11.35C9.03 11.56 9 11.78 9 12C9 13.66 10.34 15 12 15C12.22 15 12.44 14.97 12.65 14.92L14.2 16.47C13.53 16.8 12.79 17 12 17C9.24 17 7 14.76 7 12C7 11.21 7.2 10.47 7.53 9.8ZM11.84 9.02L14.99 12.17L15.01 12.01C15.01 10.35 13.67 9.01 12.01 9.01L11.84 9.02Z'
                        fill='#666'
                      />
                    </>
                  ) : (
                    <>
                      <path
                        d='M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z'
                        fill='#666'
                      />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {confirm && password !== confirm && (
              <span className='reset-password-error-text'>
                Passwords do not match
              </span>
            )}
          </div>

          <button
            type='submit'
            className='reset-password-btn-primary'
            disabled={
              passwordErrors.length > 0 ||
              password !== confirm ||
              !password ||
              isLoggingIn
            }
          >
            Reset Password & Login
          </button>

          {message && (
            <div
              className={`reset-password-message ${message.includes('✅')
                ? 'reset-password-message-success'
                : 'reset-password-message-error'
                }`}
            >
              {message}
              {message.includes('Please log in') && (
                <div className='reset-password-message-actions'>
                  <button
                    type='button'
                    className='reset-password-btn-secondary'
                    onClick={() => navigate('/login')}
                  >
                    Go to Login
                  </button>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
